

































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch,
} from '@vue/composition-api';
import type { PropType } from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import { useFormat } from '@ideavivat/vivat-2';
import PrettyCheck from 'pretty-checkbox-vue/check';

import useAuth from '@/useAuth';

import getInvoice from '@/gql/invoice/getInvoice.gql';
import createInvoice from '@/gql/invoice/createInvoice.gql';
import updateInvoice from '@/gql/invoice/updateInvoice.gql';

import CommonSelectWithCompanyList from '@/components/Invoice/CommonSelectWithCompanyList.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import AreaCode from '@/components/DeliveryNote/AreaCode.vue';

import type { Delivery } from '@/useDelivery';
import usePayment from '@/usePayment';
import useInvoiceStatus from '@/useInvoiceStatus';
import useVehicle from '@/useVehicle';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';

const CommonFormWithInvoiceEditor = defineComponent({
  components: {
    PrettyCheck,
    CommonSelectWithCompanyList,
    CommonModalWithPreset,
    AreaCode,
  },
  props: {
    id: { type: Number, default: -1 },
    deliveries: { type: Array },
    selectedItemInfo: { type: Array as PropType<Delivery[]>, default: () => ([]) },
  },
  setup(props, { emit }) {
    const NOW = useFormat.date().computerShortDate();
    const toCommaFormat = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const { me } = useAuth();
    const { th, en } = usePayment();
    const { DEFAULT_INVOICE } = useInvoiceStatus();
    const { vehicleTypeToDeliveryFee } = useVehicle();
    const { farthestDeliveryDistanceIndex } = useDeliveryAreaCode();
    const invoice = reactive({ ...DEFAULT_INVOICE });
    const filter = reactive({
      totalDeliveryFee: undefined,
      vehicleKey: '',
    });
    const toggleStatusSelect = (event: any) => {
      const status = event ? 'PAID' : 'UNPAID';
      invoice.status = status;
    };

    const invoiceStatusDropdown = ['UNPAID', 'PAID'];

    const modal = ref(null);

    const open = () => {
      (modal.value as any).open();
    };
    const close = () => {
      (modal.value as any).close();
    };

    const { result: invoiceResult, loading } = useQuery(getInvoice, computed(() => ({
      id: props.id,
    })));
    const _invoice = useResult(invoiceResult, {}, (data) => data.invoice);
    watch(() => _invoice.value, (newVal, oldVal) => {
      if (props.id !== -1 && !loading.value && newVal !== oldVal) {
        invoice.id = _invoice.value.id;
        invoice.code = _invoice.value.code;
        // invoice.date = _invoice.value.date;
        invoice.dueDate = _invoice.value.dueDate;
        invoice.type = _invoice.value.type;
        invoice.remarks = _invoice.value.remarks;
        invoice.status = _invoice.value.status;
        invoice.discount = _invoice.value.discount;
        invoice.companyId = _invoice.value.debtor.id;
      } else {
        invoice.id = -1;
        invoice.code = '';
        // invoice.date = NOW;
        invoice.dueDate = NOW;
        invoice.type = '';
        invoice.remarks = '';
        invoice.status = '';
        invoice.companyId = '';
        invoice.discount = 0;
      }
    });

    const refetchQueries = ['getInvoices', 'getDeliveries', 'getInvoiceStats', 'sumDeliveryFee'];
    const invoiceInput = computed(() => ({
      id: props.id === -1 ? undefined : props.id,
      code: props.id === -1 ? invoice.code : undefined,
      // date: useFormat.date(invoice.date).computerShortDate(),
      dueDate: useFormat.date(invoice.dueDate).computerShortDate(),
      type: invoice.type,
      remarks: invoice.remarks,
      status: props.id === -1 ? 'UNPAID' : invoice.status,
      discount: invoice.discount,
      deliveries: props.id === -1 ? { connect: props.deliveries } : undefined,
      createdBy: props.id === -1 ? { connect: me.id } : undefined,
      debtor: { connect: (invoice.companyId as any)?.id },
      realDeliveryFee: parseFloat(filter.totalDeliveryFee as any),
      // eslint-disable-next-line
      deliveryType: props.selectedItemInfo.length > 0 ? ((props as any).selectedItemInfo[0].type === 'LEASED' ? 'LEASED' : 'NORMAL') : '',
    }));

    const computedTotalPrice = computed(() => {
      const total = props.selectedItemInfo?.reduce((value, element) => value + (parseInt(element.deliveryFee as any, 10) - parseInt(element.deliveryDiscount as any, 10)), 0);
      return total;
    });
    const computedVehicleType = computed(() => {
      if (props.selectedItemInfo.length === 0) return '';
      const data = props.selectedItemInfo[0]?.deliveryNote?.vehicle?.vehicleType;
      return data;
    });
    const deliveryFeeKey = computed(() => {
      const data = vehicleTypeToDeliveryFee(computedVehicleType.value as string);
      return data;
    });
    const computedFartestDelivery = computed(() => {
      const result = farthestDeliveryDistanceIndex(props.selectedItemInfo.map((deli: Delivery) => deli.area?.distanceType || ''));
      return result;
    });
    const computedLeasedCost = computed(() => {
      if (props.selectedItemInfo.length === 0 || computedFartestDelivery.value === -1) return 0;
      const data = (props.selectedItemInfo as any)[computedFartestDelivery.value].area[deliveryFeeKey.value];
      const { area } = (props.selectedItemInfo as any)[computedFartestDelivery.value];
      const obj = { fee: data, area };
      return obj;
    });
    const createInvoiceMutation = useMutation(createInvoice, () => ({
      variables: {
        input: invoiceInput.value,
      },
      refetchQueries,
    }));
    const updateInvoiceMutation = useMutation(updateInvoice, () => ({
      variables: {
        input: {
          ...invoiceInput.value,
          code: undefined,
          delivery: undefined,
          createdBy: undefined,
        },
      },
      refetchQueries,
    }));
    const computedMutation = computed(() => {
      if (props.id !== -1) return updateInvoiceMutation;
      return createInvoiceMutation;
    });

    const confirmModal = ref(null);
    createInvoiceMutation.onDone(() => {
      emit('done');
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'สำเร็จ' });
    });
    updateInvoiceMutation.onDone(() => {
      emit('done');
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'สำเร็จ' });
    });
    const errorModal = ref(null);
    createInvoiceMutation.onError(() => {
      emit('done');
      (errorModal.value as any).compose();
    });
    updateInvoiceMutation.onError(() => {
      emit('done');
      (errorModal.value as any).compose();
    });
    // onMounted(() => {
    //   setTimeout(() => {
    //     console.log(props.selectedItemInfo);
    //   }, 15000);
    // });

    return {
      filter,
      invoice,
      invoiceDisplay: th,
      invoiceList: en,
      invoiceStatusDropdown,

      invoiceInput,

      modal,
      confirmModal,
      errorModal,
      open,
      close,

      computedMutation,

      toggleStatusSelect,

      computedTotalPrice,
      toCommaFormat,
      computedVehicleType,
      deliveryFeeKey,
      computedLeasedCost,
    };
  },
});

export default CommonFormWithInvoiceEditor;
